import React, { useState, useEffect, useContext } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import { useGet, usePut } from '../../../sharedModules/hooks/useQuery';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface EmailTemplateResponse {
  result: { subject?: string; body?: string };
}

const EMAIL_PREVIEW_STYLES = `
  body { font-family: Arial, sans-serif; margin: 0; padding: 0; background-color: #f4f4f4; }
  .wrapper { max-width: 600px; margin: 0 auto; background-color: #ffffff; }
  .header { background-color: #ffffff; padding: 12px 24px; text-align: center; border-bottom: 1px solid #e0e0e0; }
  .header img { max-width: 100%; height: auto; max-height: 48px; display: block; margin: 0 auto; }
  .body { padding: 28px; color: #333333; font-size: 15px; line-height: 1.6; }
  .footer { background-color: #F44336; padding: 12px 24px; text-align: center; font-size: 12px; color: #fff; border-top: 1px solid #e0e0e0; }
  .footer a { color: #fff; text-decoration: none; }
`;

const buildPreviewHtml = (body: string): string => `
<!DOCTYPE html>
<html>
  <head><meta charset="UTF-8"><style>${EMAIL_PREVIEW_STYLES}</style></head>
  <body>
    <div class="wrapper">
      <div class="header">
        <img src="/tudi-mechanical-systems-logo.png" alt="TUDI Mechanical Systems" />
      </div>
      <div class="body">${body}</div>
      <div class="footer">
        <p>TUDI &bull; <a href="mailto:info@tudi.com">info@tudi.com</a></p>
      </div>
    </div>
  </body>
</html>
`;

const QUILL_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
};

const CustomerNotificationPage: React.FC = () => {
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const { data: EmailTemplateResponse, isLoading, error } = useGet<EmailTemplateResponse>({
    url: '/home_services/email_template',
  });

  const { mutateAsync, isPending } = usePut();

  useEffect(() => {
    const result = EmailTemplateResponse?.data?.result;
    if (result) {
      setSubject(result.subject ?? '');
      setBody(result.body ?? '');
    }
  }, [EmailTemplateResponse]);

  const errorMessage = error ? 'Failed to load email template.' : '';
  const loading = isLoading || isPending;

  const handleSave = async () => {
    try {
      await mutateAsync({
        url: '/home_services/email_template',
        data: { email_template: { subject, body } },
      });
      showSuccessSnackBar?.('Email template saved.');
    } catch {
      showErrorSnackBar?.('Failed to save email template.');
    }
  };

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <Grid container spacing={3} style={{ padding: '1% 2%' }}>
        <Grid item xs={12}>
          <PageHeader mainTitle="Home Services" subTitle="Customer Notification" />
        </Grid>

        {errorMessage && (
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>
        )}

        {/* Left: editor */}
        <Grid item xs={12} md={6}>
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>
              Email Configuration
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            <TextField
              label="Email Subject"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={{ marginBottom: 24 }}
            />

            <Typography variant="body2" color="textSecondary" gutterBottom>
              Email Body
            </Typography>
            <ReactQuill
              theme="snow"
              value={body}
              onChange={setBody}
              modules={QUILL_MODULES}
              style={{ height: 300, marginBottom: 48 }}
            />

            <Button variant="contained" color="primary" onClick={handleSave} style={{ marginTop: 8 }}>
              Save Template
            </Button>
          </Paper>
        </Grid>

        {/* Right: live preview */}
        <Grid item xs={12} md={6}>
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>
              Email Preview
            </Typography>
            <Divider style={{ marginBottom: 16 }} />
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom style={{ fontSize: 16 }}>
              Subject: <strong>{subject || '(no subject)'}</strong>
            </Typography>
            <iframe
              key={body ? 'filled' : 'empty'}
              srcDoc={buildPreviewHtml(body)}
              sandbox=""
              title="Email preview"
              style={{ width: '100%', height: 460, border: '1px solid #e0e0e0', borderRadius: 4 }}
            />
          </Paper>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default CustomerNotificationPage;
